import {FC, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
//mui
import {Box, IconButton, Typography, useMediaQuery} from '@mui/material';
//icons
import {ReactComponent as Logo} from '@icons/logo.svg';
import {ReactComponent as Notification} from '@icons/notification-new.svg';
//constants
import {BODY1, SUBTITLE2} from '@/utils/constants';
//components;
import ProgressBar from '@components/ProgressBar';
import OrganizationDropdown from './OrganizationDropdown';
import OrganizationEventSelect from './OrganizationEventSelect';
import NotificationPopup from './OrganizationNotificationPopup';
//enums
import {ROUTER_URL} from '@/utils/enums';
//styles
import {organizationHeaderStyle} from './OrganizationHeader.styles';
import {useSelector} from 'react-redux';
import {getDashboardStatistics, getDashboardStatisticsSelector} from '@/store/organization-dashboard';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import Loader from '@/shared/components/Loader';
import {currencyFormatter} from '@/utils/helpers/currency';
import {unreadNotificationSelector} from '@/store/notification';

const OrganizationHeader: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {notifications: unreadNotifications} = useAppSelector(unreadNotificationSelector);

  const currentEvent = useSelector(getCurrentEventSelector);
  const eventStatistic = useSelector(getDashboardStatisticsSelector);

  const isLaptop = useMediaQuery('(min-width: 1200px)');

  const progressValue = currentEvent?.isGoalAmount
    ? currentEvent?.goalAmount > (currentEvent?.raisedAmount ?? 0)
      ? ((currentEvent?.raisedAmount ?? 0) / currentEvent?.goalAmount) * 100
      : 100
    : (currentEvent?.raisedAmount ?? 0) > 0
    ? 100
    : 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isLaptop) navigate(ROUTER_URL.ORGANIZATION_NOTIFICATIONS);
    else setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAllClick = () => {
    navigate(ROUTER_URL.ORGANIZATION_NOTIFICATIONS);
    handleClose();
  };

  useEffect(() => {
    if (!currentEvent) return;
    setIsLoading(true);
    const getStatistic = async () => {
      try {
        await dispatch(getDashboardStatistics(currentEvent?.id)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };

    getStatistic();
  }, [eventStatistic?.moneyColl, eventStatistic?.totalMoney, currentEvent?.id]);

  const open = !!anchorEl;
  const id = open ? 'simple-popover' : undefined;
  return (
    <Box sx={organizationHeaderStyle.header}>
      <Box sx={organizationHeaderStyle.panel}>
        <Box sx={organizationHeaderStyle.logo}>
          <Link to={ROUTER_URL.ORGANIZATION}>
            <Logo />
          </Link>
        </Box>

        <Box sx={organizationHeaderStyle.panelInfo}>
          <Box sx={organizationHeaderStyle.panelCenter}>
            <OrganizationEventSelect />

            {isLoading ? (
              <Loader />
            ) : (
              currentEvent && (
                <Box sx={organizationHeaderStyle.progressWrap}>
                  <Box sx={organizationHeaderStyle.progressValue}>
                    <Typography variant={BODY1} sx={organizationHeaderStyle.progressTargetAmount}>
                      {` ${currencyFormatter.format(currentEvent?.raisedAmount)}`}
                    </Typography>
                    {currentEvent?.isGoalAmount && (
                      <Typography sx={organizationHeaderStyle.progressTargetGoal} variant={SUBTITLE2}>
                        {`	of ${currencyFormatter.format(currentEvent?.goalAmount)} goal`}
                      </Typography>
                    )}
                  </Box>
                  <ProgressBar value={progressValue} />
                </Box>
              )
            )}
          </Box>

          <Box sx={organizationHeaderStyle.user}>
            <IconButton aria-describedby={id} sx={organizationHeaderStyle.notificationButton} onClick={handleClick}>
              {unreadNotifications.length > 0 ? (
                <Notification />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g id="notification">
                    <path
                      d="M12.0199 20.5302C9.68987 20.5302 7.35987 20.1602 5.14987 19.4202C4.30987 19.1302 3.66987 18.5402 3.38987 17.7702C3.09987 17.0002 3.19987 16.1502 3.65987 15.3902L4.80987 13.4802C5.04987 13.0802 5.26987 12.2802 5.26987 11.8102V8.92018C5.26987 5.20018 8.29987 2.17018 12.0199 2.17018C15.7399 2.17018 18.7699 5.20018 18.7699 8.92018V11.8102C18.7699 12.2702 18.9899 13.0802 19.2299 13.4902L20.3699 15.3902C20.7999 16.1102 20.8799 16.9802 20.5899 17.7702C20.2999 18.5602 19.6699 19.1602 18.8799 19.4202C16.6799 20.1602 14.3499 20.5302 12.0199 20.5302ZM12.0199 3.67018C9.12987 3.67018 6.76987 6.02018 6.76987 8.92018V11.8102C6.76987 12.5402 6.46987 13.6202 6.09987 14.2502L4.94987 16.1602C4.72987 16.5302 4.66987 16.9202 4.79987 17.2502C4.91987 17.5902 5.21987 17.8502 5.62987 17.9902C9.80987 19.3902 14.2399 19.3902 18.4199 17.9902C18.7799 17.8702 19.0599 17.6002 19.1899 17.2402C19.3199 16.8802 19.2899 16.4902 19.0899 16.1602L17.9399 14.2502C17.5599 13.6002 17.2699 12.5302 17.2699 11.8002V8.92018C17.2699 6.02018 14.9199 3.67018 12.0199 3.67018Z"
                      fill="#0C1525"
                    />
                    <path
                      d="M13.8795 3.94018C13.8095 3.94018 13.7395 3.93018 13.6695 3.91018C13.3795 3.83018 13.0995 3.77018 12.8295 3.73018C11.9795 3.62018 11.1595 3.68018 10.3895 3.91018C10.1095 4.00018 9.80949 3.91018 9.61949 3.70018C9.42949 3.49018 9.36949 3.19018 9.47949 2.92018C9.88949 1.87018 10.8895 1.18018 12.0295 1.18018C13.1695 1.18018 14.1695 1.86018 14.5795 2.92018C14.6795 3.19018 14.6295 3.49018 14.4395 3.70018C14.2895 3.86018 14.0795 3.94018 13.8795 3.94018Z"
                      fill="#0C1525"
                    />
                    <path
                      d="M12.0194 22.8102C11.0294 22.8102 10.0694 22.4102 9.3694 21.7102C8.6694 21.0102 8.2694 20.0502 8.2694 19.0602H9.7694C9.7694 19.6502 10.0094 20.2302 10.4294 20.6502C10.8494 21.0702 11.4294 21.3102 12.0194 21.3102C13.2594 21.3102 14.2694 20.3002 14.2694 19.0602H15.7694C15.7694 21.1302 14.0894 22.8102 12.0194 22.8102Z"
                      fill="#0C1525"
                    />
                  </g>
                </svg>
              )}
            </IconButton>
            <NotificationPopup
              id={id}
              open={open}
              anchorEl={anchorEl}
              handleClose={handleClose}
              handleLinkClick={handleViewAllClick}
            />
            <OrganizationDropdown />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationHeader;
